import React, { useEffect, useState, useRef } from "react";
import { message } from "antd";
import { get_errors, edit_errors} from "../services";
import PropTypes from "prop-types";
import { ErrorNotFounded, ErrorsView, FixErrorDrawer } from "../components/error_pool";
import { useCalculateListSizeForPaginationHook } from "../hooks";
import { CircleAnimationCompact } from "../components/loading";

const empty_report_error = {
	id: "",
	name_process: "",
	error_verified: true,
	date: "",
	verified_by: "",
	date_of_verification: "",
	type: "",
	errors_types_detected: [],
	documents_id: [],
	specs: {}
};

const default_parameters = {
	name_process: null,
	error_verified: false,
	type: null,
};

const massive_elements_limit = 50;

export const ErrorPool = (props) => {
	const [errorsReports, setErrorsReports] = useState([]);
	const [backupErrorsReports, setBackupErrorsReports] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selected, setSelected] = useState(empty_report_error);
	const [openEdit, setOpenEdit] = useState(false);
	const [seeErrorsReports, setSeeErrorsReports] = useState(false);
	const [hoveredItem, setHoveredItem] = useState(null);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	
	const { pageSize } = useCalculateListSizeForPaginationHook();

	const [filterOptions, setFilterOptions] = useState({
		"types_errors":[],
		"process_errors":[],
	});
	const [selectedOptions, setSelectedOptions] = useState(default_parameters);
	const showMessageRef = useRef(false);

	const [massiveCheckedList, setMassiveCheckedList] = useState([]);
	const checkAll = errorsReports.length === massiveCheckedList.length;
	const indeterminate = massiveCheckedList.length > 0 && massiveCheckedList.length < errorsReports.length;

	const loadDataAndOrUpdateParameters = (attribute, selectedValue, data_to_load) => {
		const parameters = (attribute === undefined || selectedValue === undefined) ? 
			selectedOptions : {...selectedOptions, [attribute]: selectedValue};

		const data = (data_to_load) ? data_to_load : backupErrorsReports;
		
		const filteredData = data.filter(item => {
			return Object.keys(parameters).every(key => {				
				if (key === "name_process" && parameters[key] !== null) {
					return item.name_process === parameters[key];
				}
				if (key === "type" && parameters[key] !== null) {
					return item.type === parameters[key];
				}
				if (key === "error_verified") {															
					return item.error_verified === (parameters[key] === true);
				}
				
				return true;
			});
		});

		setErrorsReports(filteredData);	
		setSelectedOptions(parameters);
		return filteredData;
	};
	
	const fetchData = async () => {		
		setLoading(true);
		setSeeErrorsReports(false);		
		const res_get_errors = await get_errors(props.token, selectedOptions.error_verified);
		if(res_get_errors){
			setMassiveCheckedList([]);
			setErrorsReports(res_get_errors);
			setBackupErrorsReports(res_get_errors);
			loadDataAndOrUpdateParameters("error_verified", selectedOptions.error_verified, res_get_errors);
			
			const counter = res_get_errors.length;
			const msg = (counter > 0) ? `Se cargaron ${counter} Errores` : "No se encontraron Errores";
			message.success(msg);
											
			setFilterOptions({
				"types_errors": uniqueElementsArray(res_get_errors, "type"),
				"process_errors": uniqueElementsArray(res_get_errors, "name_process"),
			});

			setSeeErrorsReports((counter > 0));			
		}
		
		setLoading(false);
	};
	
	const updateStatusError = async () => {
		setLoadingSubmit(true);
		setLoading(true);
		const data_to_send = (massiveCheckedList.length > 1 && selectedOptions.error_verified === false) 
			? massiveCheckedList : [selected.id];

		const res_edit_error = await edit_errors(props.token, data_to_send);
		if(res_edit_error){
			const filteredIds = data_to_send.filter(id => {
				const isInError = res_edit_error.some(error => error.id === id);
				return !isInError;
			});

			res_edit_error.map(error => {
				if (error.message) {
					message.error(`The error with id ${error.id} couldn't be modified because ${error.message}`);
				}
			}
			);

			const new_errors = backupErrorsReports.map(error => 
				filteredIds.includes(error.id)? {...error, error_verified: true, verified_by: props.user.email} : error
			);

			setMassiveCheckedList([]);

			setBackupErrorsReports(new_errors);
			loadDataAndOrUpdateParameters("error_verified", selectedOptions.error_verified, new_errors);
			setSeeErrorsReports((new_errors.length > 0));	
			onClose();
		}
		
		setLoadingSubmit(false);
		setLoading(false);
	};

	const uniqueElementsArray = (data, attribute) => {
		const uniqueTypesErrors = new Set();
		data.forEach(obj => uniqueTypesErrors.add(obj[attribute]));
		const uniqueTypesErrorsArray = [...uniqueTypesErrors];

		const arrayUnique = uniqueTypesErrorsArray.map(type => ({
			value: type,
			label: type,
		}));
								
		return [{value:null, label:"Todos"}, ...arrayUnique];
	};

	const showEdit = () => {
		setOpenEdit(true);		
	};

	const onClose = () => {
		setOpenEdit(false);
		setSelected(empty_report_error);
		setHoveredItem(null);
	};
	
	const onChangeSearch = (value) => {
		const text = value.target.value;

		const matchesText = (str) => {
			if (typeof str === "string") {
				return str.toLowerCase().includes(text.toLowerCase());
			}
			return false;
		};

		const checkObjectForMatch = (obj) => {
			for (const key in obj) {
				if (matchesText(obj[key])) {
					return true;
				}

				if (typeof obj[key] === "object" && obj[key] !== null) {
					if (checkObjectForMatch(obj[key])) {
						return true;
					}
				}
			}
			return false;
		};

		if(text.trim() === "") {
			setErrorsReports(loadDataAndOrUpdateParameters());
			return;
		}

		const result = loadDataAndOrUpdateParameters().filter((invoice) =>
			checkObjectForMatch(invoice)
		);
		setErrorsReports(result);
	};

	const onRangeChange = (dateRange) => {
		const data = loadDataAndOrUpdateParameters();
		
		if (!dateRange || dateRange.length !== 2) {			
			return data;
		}

		const [startDate, endDate] = dateRange;

		const filteredData = data.filter(
			(item) =>
				new Date(item.date) >= startDate.startOf("day") &&
				new Date(item.date) <= endDate.endOf("day")
		);

		setErrorsReports(filteredData);
	};
	
	const onChangeElementSelected = (e, invoice_id) => {		
		if(massiveCheckedList.length > massive_elements_limit && e.target.checked){ // Check the limit
			message.warning(`El límite de facturas a mandar de forma masiva es de ${massive_elements_limit}`);
			return;
		}
		
		if(e.target.checked){ // Add Element
			setMassiveCheckedList([...massiveCheckedList, invoice_id]);
			return;
		}
		
		setMassiveCheckedList( // Remove id invoice
			massiveCheckedList.filter((checkList) => {
				return checkList !== invoice_id;
			})
		);		
	};	

	const onChangeAllSelect = (e) => {
		if(massiveCheckedList.length === massive_elements_limit){
			setMassiveCheckedList([]);
			return;
		}
		
		const limitedInvoices = errorsReports.slice(0, massive_elements_limit);
		setMassiveCheckedList(e.target.checked ? limitedInvoices.map((invoice_id) => invoice_id.id) : []);
	};

	useEffect(() => {		
		fetchData();		
	}, []);
	
	return (
		<>
			{
				loading &&
				<CircleAnimationCompact/>
			}
			{
				!loading &&
				<>
					{(!seeErrorsReports) && <ErrorNotFounded setSeeErrorsReports={setSeeErrorsReports}/>}
					{(seeErrorsReports) && <ErrorsView
						filterOptions={filterOptions}
						selectedOptions ={selectedOptions}
						loadDataAndOrUpdateParameters={loadDataAndOrUpdateParameters}
						onRangeChange={onRangeChange}
						showMessageRef={showMessageRef}
						fetchData={fetchData}
						onChangeSearch={onChangeSearch}
						indeterminate={indeterminate}
						checkAll={checkAll}
						onChangeAllSelect={onChangeAllSelect}
						massiveCheckedList={massiveCheckedList}
						errorsReports={errorsReports}
						updateStatusError={updateStatusError}
						loadingSubmit={loadingSubmit}
						pageSize={pageSize}
						onChangeElementSelected={onChangeElementSelected}
						setHoveredItem={setHoveredItem}
						selected={selected}
						hoveredItem ={hoveredItem }
						setSelected={setSelected}
						showEdit={showEdit}
						empty_report_error={empty_report_error}
						loading={loading}
					/>}
				</>
			}
			<FixErrorDrawer
				hoveredItem={hoveredItem}
				setHoveredItem={setHoveredItem}
				setSelected={setSelected}
				errorsReports={errorsReports}
				onClose={onClose} 
				openEdit={openEdit} 
				selected={selected} 
				selectedOptions={selectedOptions} 
				massiveCheckedList={massiveCheckedList} 
				onChangeElementSelected={onChangeElementSelected}
				updateStatusError={updateStatusError}
				loadingSubmit={loadingSubmit}
			/>			
		</>
	);
};

ErrorPool.propTypes = {
	token: PropTypes.string.isRequired,
	user: PropTypes.shape({
		email: PropTypes.string.isRequired,		
	}),
};
